// The ONE place that decides which email address to SHOW for a member (0125).
//
// Why this is a module and not `m.displayEmail ?? m.email` inline at each call
// site: there are a dozen surfaces that render a member's address (rosters,
// pickers, the profile panel, quick-switcher name fallbacks), and the two fields
// mean very different things:
//
//   member.email        — the account's IDENTITY ANCHOR. Stable across
//                         workspaces. Cross-daemon initiated-by matching and
//                         user dedupe key on it, so it must never follow a
//                         display choice.
//   member.displayEmail — what this member chose to show IN THIS WORKSPACE,
//                         already resolved server-side (per-workspace pick →
//                         account default → anchor).
//
// Reaching for `.email` is the natural, wrong habit — it is the older field and
// it is never null. The per-workspace AVATAR shipped with exactly that mistake
// in seven places and leaked one workspace's photo into another. So: one
// resolver, and `member-email.test.ts` fails the build if a component
// re-implements the fallback chain itself.

/** The subset any member-shaped row satisfies; keeps this usable for the
 *  workspace roster, channel members and DM peers alike. */
export interface MemberEmailFields {
  email?: string | null;
  displayEmail?: string | null;
}

/**
 * The address to display for this member in the CURRENT workspace.
 *
 * Falls back to the anchor so the result is never empty — an older relay that
 * does not send `displayEmail` renders exactly as it did before.
 */
export function memberEmail(member: MemberEmailFields | null | undefined): string {
  return member?.displayEmail ?? member?.email ?? "";
}

/**
 * The local part, for the "no display name set" label fallback
 * (`name ?? email.split("@")[0]`), resolved through the same rule.
 */
export function memberEmailLocalPart(member: MemberEmailFields | null | undefined): string {
  return memberEmail(member).split("@")[0] ?? "";
}
